import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { GoToStoreOutlineSVG } from 'components/iconsSVG/goToStoreOutline';
import MapComponent from 'components/map';
import { useI18n } from 'context/i18n';
import { getAllStores } from 'actions/shops';
import { queryKeys } from 'utils/constants/reactQuery';

import { Dialog, DialogHeader, DialogRoot } from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { ProgressCircle } from 'components/ui/progress-circle';
import { Link } from 'components/ui/link';

type Store = {
  id: string;
  name: string;
  address1: string;
  postalCode: string;
  cityName: string;
  telephone: string;
  latitude: number;
  longitude: number;
  urlGoogleMaps?: string;
};

type StoreLocatorOptions = {
  type: 'repair' | string;
  title: string;
};

function calculateAverage(storesArray: Store[]): [number, number] {
  return storesArray
    .reduce(
      ([lat, long], store) => {
        const newCoords: [number, number] = [(lat ?? 0) + store.latitude, (long ?? 0) + store.longitude];
        return newCoords;
      },
      [0, 0],
    )
    .map((n) => n / storesArray.length) as [number, number];
}

export function StoresModal() {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<StoreLocatorOptions>({ type: 'repair', title: '' });
  const { data, status } = useQuery<Store[], Error>({
    queryKey: [queryKeys.stores.all, locale, options.type],
    queryFn: async () => {
      const stores = await getAllStores({ profile: locale, repairProgram: options.type === 'repair' });
      // Ensure the result is an array of Store
      if (!Array.isArray(stores)) {
        throw new Error('Invalid stores data');
      }
      return stores as Store[];
    },
    enabled: open,
  });

  const [view, setView] = useState<'list' | 'map'>('list');
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const filteredStores = useMemo(() => data ?? [], [data]);

  useEffect(() => {
    window.showStoreLocator = ({ type, title }: StoreLocatorOptions = { type: 'repair', title: '' }) => {
      setOptions({ type, title });
      setOpen(true);
    };
    return () => {
      delete window.showStoreLocator;
    };
  }, []);

  const handleClickMarker = (coords: [number, number]) => {
    const foundStore = filteredStores.find((s) => s.latitude === coords[0] && s.longitude === coords[1]);
    if (foundStore) {
      setSelectedStore(foundStore);
      setView('list');

      setTimeout(() => {
        const foundDiv = document.querySelector<HTMLDivElement>(`[data-store="${foundStore.id}"]`);
        if (foundDiv) {
          foundDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    }
  };

  const MapMemo = useMemo(
    () => (
      <MapComponent
        sticky={false}
        center={
          !filteredStores.length ? [0, 0]
          : selectedStore === null ?
            calculateAverage(filteredStores)
          : [selectedStore.latitude, selectedStore.longitude]
        }
        visible={true}
        viewStoreInMap={false}
        pins={filteredStores.map(({ latitude, longitude }) => ({
          latitude,
          longitude,
        }))}
        onClickMarker={handleClickMarker}
        markerProvider={{ iconUrl: '/assets-new/nn-locator-marker.svg' as TODO, iconSize: [21, 32] }}
        selectedMarkerProvider={{ iconUrl: '/assets-new/nn-locator-marker-selected.svg' as TODO, iconSize: [21, 32] }}
        zoom={selectedStore ? 12 : 5}
        onZoom={() => {}}
      />
    ),
    [filteredStores, selectedStore],
  );

  return (
    <DialogRoot isOpen={open} onOpenChange={setOpen} showOverlay>
      <Dialog>
        <div className="flex flex-col gap-2.5">
          <DialogHeader label={options.title || 'Stores locator'} hidden canClose={true} />
          <div className="flex h-[500px] flex-col">
            {status === 'pending' && (
              <div className="flex flex-grow items-center justify-center">
                <ProgressCircle />
              </div>
            )}
            {status === 'success' && view === 'map' && (
              <div data-view={view} className="h-[500px]">
                {MapMemo}
              </div>
            )}
            {status === 'success' && view === 'list' && (
              <div id="modalStoresList" data-view={view} className="h-full overflow-auto">
                {data?.map((store) => (
                  <div
                    key={`store-${store.id}`}
                    className="border-b border-subtle px-1 py-2.5 data-[active=true]:bg-subtle [&_p]:mb-0"
                    data-store={store.id}
                    data-active={selectedStore && selectedStore.id === store.id}
                  >
                    <p className="flex items-center gap-2">
                      <img src="/assets-new/no_trace_program/nn-minilogo.png" alt="nnormal logo" width={21} />
                      <span>{store.name}</span>
                    </p>
                    <div className="flex flex-col pl-[30px] text-xs text-muted">
                      <p>{store.address1}</p>
                      <p>
                        {store.postalCode}, {store.cityName}
                      </p>
                      <p>{store.telephone}</p>
                    </div>
                    <Button
                      variant="none"
                      className="ml-[30px] text-xxs uppercase transition-colors hover:text-muted [&>span]:underline"
                      // className={styles.viewInMap}
                      onClick={() => {
                        setSelectedStore(store);
                        setView('map');
                      }}
                    >
                      {t('tiendas.locator', 'view.in.map', 'View in map')}
                    </Button>
                    <Link
                      variant="none"
                      href={store.urlGoogleMaps || `https://maps.google.com/?q=${store.latitude},${store.longitude}`}
                      target="_blank"
                      className="flex items-center gap-2 text-xs"
                      // className={styles.viewInGoogle}
                      rel="noreferrer"
                    >
                      <GoToStoreOutlineSVG className="size-5" />
                      <span>{t('generico', 'go.to.store', 'GO TO STORE')}</span>
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            {view === 'map' ?
              <Button variant="solid" onPress={() => setView('list')}>
                {t('generico', 'view.list', 'View List')}
              </Button>
            : <Button
                variant="solid"
                onPress={() => {
                  setSelectedStore(null);
                  setView('map');
                }}
              >
                {t('generico', 'view.map', 'View Map')}
              </Button>
            }
          </div>
        </div>
      </Dialog>
    </DialogRoot>
  );
}
